import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth, formatErr } from "@/context/AuthContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import {
  Tree, Plus, TrashSimple, ArrowLeft, CaretRight, CaretDown, FloppyDisk,
  Copy, Download, Trash, Warning, Package as PackageIcon,
} from "@phosphor-icons/react";

const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
const canManageRoles = new Set(["yonetici"]);

// Recursive tree manipulation helpers
function walkAndUpdate(items, id, updater) {
  return items.map(it => {
    if (it.id === id) return updater(it);
    if (it.children?.length) return { ...it, children: walkAndUpdate(it.children, id, updater) };
    return it;
  });
}
function walkAndRemove(items, id) {
  return items.filter(it => it.id !== id).map(it =>
    it.children?.length ? { ...it, children: walkAndRemove(it.children, id) } : it
  );
}
function walkAndInsertChild(items, parentId, child) {
  return items.map(it => {
    if (it.id === parentId) {
      return { ...it, children: [...(it.children || []), child] };
    }
    if (it.children?.length) return { ...it, children: walkAndInsertChild(it.children, parentId, child) };
    return it;
  });
}
function countAndTotal(items) {
  let count = 0, total = 0;
  for (const it of items || []) {
    count += 1;
    total += (Number(it.planned_qty) || 0) * (Number(it.unit_price) || 0);
    const c = countAndTotal(it.children || []);
    count += c.count; total += c.total;
  }
  return { count, total };
}

function WBSRow({ item, depth, onUpdate, onDelete, onAddChild, canManage, expanded, toggle }) {
  const hasChildren = item.children?.length > 0;
  const isExpanded = expanded.has(item.id);
  const rowTotal = (Number(item.planned_qty) || 0) * (Number(item.unit_price) || 0);
  return (
    <>
      <tr data-testid={`wbs-row-${item.id}`} className={depth === 0 ? "bg-slate-50/70 border-t-2 border-slate-300" : "border-t border-slate-100"}>
        <td className="px-2 py-1.5" style={{ paddingLeft: `${depth * 18 + 8}px` }}>
          <div className="flex items-center gap-1">
            {hasChildren ? (
              <button onClick={() => toggle(item.id)} className="w-4 h-4 flex items-center justify-center text-slate-500 hover:text-slate-800">
                {isExpanded ? <CaretDown size={11} weight="bold" /> : <CaretRight size={11} weight="bold" />}
              </button>
            ) : <span className="w-4" />}
            <Input value={item.code}
                   data-testid={`wbs-code-${item.id}`}
                   onChange={e => onUpdate(item.id, i => ({ ...i, code: e.target.value }))}
                   placeholder="Kod"
                   disabled={!canManage}
                   className="h-7 rounded-none sm:rounded-sm text-xs font-mono w-20" />
          </div>
        </td>
        <td className="px-2 py-1.5">
          <Input value={item.name}
                 data-testid={`wbs-name-${item.id}`}
                 onChange={e => onUpdate(item.id, i => ({ ...i, name: e.target.value }))}
                 placeholder="İmalat adı"
                 disabled={!canManage}
                 className={`h-7 rounded-none sm:rounded-sm ${depth === 0 ? "font-bold" : ""}`} />
        </td>
        <td className="px-2 py-1.5">
          <Input value={item.unit}
                 data-testid={`wbs-unit-${item.id}`}
                 onChange={e => onUpdate(item.id, i => ({ ...i, unit: e.target.value }))}
                 placeholder="birim"
                 disabled={!canManage}
                 className="h-7 rounded-none sm:rounded-sm text-xs w-20 font-mono" />
        </td>
        <td className="px-2 py-1.5">
          <Input type="number" value={item.planned_qty || ""}
                 data-testid={`wbs-qty-${item.id}`}
                 onChange={e => onUpdate(item.id, i => ({ ...i, planned_qty: parseFloat(e.target.value) || 0 }))}
                 disabled={!canManage}
                 min="0" step="0.01"
                 className="h-7 rounded-none sm:rounded-sm text-right w-24 font-mono" />
        </td>
        <td className="px-2 py-1.5">
          <Input type="number" value={item.unit_price || ""}
                 data-testid={`wbs-price-${item.id}`}
                 onChange={e => onUpdate(item.id, i => ({ ...i, unit_price: parseFloat(e.target.value) || 0 }))}
                 disabled={!canManage}
                 min="0" step="0.01"
                 className="h-7 rounded-none sm:rounded-sm text-right w-28 font-mono" />
        </td>
        <td className="px-1 py-1.5">
          <Input type="date" value={item.planned_start || ""}
                 data-testid={`wbs-pstart-${item.id}`}
                 onChange={e => onUpdate(item.id, i => ({ ...i, planned_start: e.target.value }))}
                 disabled={!canManage}
                 className="h-7 rounded-none sm:rounded-sm text-xs w-32 font-mono" />
        </td>
        <td className="px-1 py-1.5">
          <Input type="date" value={item.planned_end || ""}
                 data-testid={`wbs-pend-${item.id}`}
                 onChange={e => onUpdate(item.id, i => ({ ...i, planned_end: e.target.value }))}
                 disabled={!canManage}
                 className="h-7 rounded-none sm:rounded-sm text-xs w-32 font-mono" />
        </td>
        <td className="px-2 py-1.5 text-right font-mono text-xs text-slate-700 whitespace-nowrap">
          {rowTotal > 0 ? rowTotal.toLocaleString("tr-TR", { maximumFractionDigits: 2 }) : "-"}
        </td>
        {canManage && (
          <td className="px-2 py-1.5 whitespace-nowrap">
            <button onClick={() => onAddChild(item.id)}
                    data-testid={`wbs-add-child-${item.id}`}
                    title="Alt kalem ekle"
                    className="w-6 h-6 inline-flex items-center justify-center text-[#95C11F] hover:bg-[#F7FBE9]">
              <Plus size={11} weight="bold" />
            </button>
            <button onClick={() => onDelete(item.id)}
                    data-testid={`wbs-del-${item.id}`}
                    title="Sil"
                    className="w-6 h-6 inline-flex items-center justify-center text-red-600 hover:bg-red-50">
              <TrashSimple size={11} />
            </button>
          </td>
        )}
      </tr>
      {isExpanded && item.children?.map(ch =>
        React.createElement(WBSRow, {
          key: ch.id, item: ch, depth: depth + 1,
          onUpdate, onDelete, onAddChild, canManage, expanded, toggle,
        })
      )}
    </>
  );
}

export default function BlockWBS() {
  const { siteId, blockId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const askConfirm = useConfirm();
  const canManage = !!user;

  const [meta, setMeta] = useState({ block_name: "", block_code: "" });
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [expanded, setExpanded] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const [templateDialog, setTemplateDialog] = useState(false);
  const [saveTplDialog, setSaveTplDialog] = useState(false);
  const [saveTplForm, setSaveTplForm] = useState({ name: "", description: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [w, t] = await Promise.all([
        axios.get(`${API}/blocks/${blockId}/wbs`),
        axios.get(`${API}/wbs/templates`),
      ]);
      setMeta({ block_name: w.data.block_name, block_code: w.data.block_code });
      setItems(w.data.items || []);
      // Kök ve ilk seviye açık başlasın
      setExpanded(new Set((w.data.items || []).flatMap(it => [it.id, ...(it.children || []).map(c => c.id)])));
      setTemplates(t.data);
      setDirty(false);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [blockId]);

  const toggle = (id) => {
    setExpanded(s => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };
  const expandAll = () => {
    const all = new Set();
    const walk = (arr) => arr.forEach(it => { all.add(it.id); walk(it.children || []); });
    walk(items);
    setExpanded(all);
  };
  const collapseAll = () => setExpanded(new Set());

  const onUpdate = (id, updater) => {
    setItems(prev => walkAndUpdate(prev, id, updater));
    setDirty(true);
  };
  const onDelete = async (id) => {
    const ok = await askConfirm({
      title: "Kalemi Sil",
      description: "Bu kalem ve tüm alt kalemleri silinecek. Emin misiniz?",
      destructive: true, confirmText: "Sil",
    });
    if (!ok) return;
    setItems(prev => walkAndRemove(prev, id));
    setDirty(true);
  };
  const onAddChild = (parentId) => {
    const child = { id: uuid(), code: "", name: "", unit: "", planned_qty: 0, unit_price: 0, order: 0, notes: "", children: [] };
    setItems(prev => walkAndInsertChild(prev, parentId, child));
    setExpanded(s => new Set([...s, parentId, child.id]));
    setDirty(true);
  };
  const addRoot = () => {
    const it = { id: uuid(), code: "", name: "", unit: "", planned_qty: 0, unit_price: 0, order: items.length + 1, notes: "", children: [] };
    setItems(prev => [...prev, it]);
    setDirty(true);
  };
  const clearAll = async () => {
    const ok = await askConfirm({
      title: "Tüm Kalemleri Sil",
      description: "Bu blokta tanımlı tüm iş kırınım kalemleri silinecek. Emin misiniz?",
      destructive: true, confirmText: "Tümünü Sil",
    });
    if (!ok) return;
    setItems([]);
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/blocks/${blockId}/wbs`, { items });
      toast.success("İş kırınımı kaydedildi");
      setDirty(false);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  const applyTemplate = async (tpl) => {
    if (items.length > 0) {
      const ok = await askConfirm({
        title: "Şablonu Uygula",
        description: `Mevcut ${items.length} kök kalem üzerine yazılacak. Devam edilsin mi?`,
        destructive: true, confirmText: "Uygula",
      });
      if (!ok) return;
    }
    try {
      const { data } = await axios.post(`${API}/blocks/${blockId}/wbs/from-template/${tpl.id}`);
      setItems(data.items || []);
      setExpanded(new Set((data.items || []).flatMap(it => [it.id, ...(it.children || []).map(c => c.id)])));
      setDirty(false);
      setTemplateDialog(false);
      toast.success(`"${tpl.name}" şablonu uygulandı — ${data.items_count} kalem`);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const saveAsTemplate = async () => {
    if (!saveTplForm.name.trim()) { toast.error("Şablon adı zorunlu"); return; }
    if (items.length === 0) { toast.error("Boş WBS şablon olarak kaydedilemez"); return; }
    if (dirty) { toast.error("Önce mevcut değişiklikleri kaydedin"); return; }
    try {
      await axios.post(`${API}/blocks/${blockId}/wbs/save-as-template`, {
        name: saveTplForm.name.trim(),
        description: saveTplForm.description.trim(),
      });
      toast.success("Şablon olarak kaydedildi");
      setSaveTplDialog(false);
      setSaveTplForm({ name: "", description: "" });
      // Şablon listesini yenile
      const t = await axios.get(`${API}/wbs/templates`);
      setTemplates(t.data);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const stats = useMemo(() => countAndTotal(items), [items]);

  return (
    <Shell
      title={meta.block_name ? `${meta.block_name} — İş Kırınımı` : "Yükleniyor…"}
      subtitle="Proje İlerleme › WBS"
      hideSiteSelector
      actions={
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/proje/${siteId}/yapisi`)}
                  className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest">
            <ArrowLeft size={14} className="mr-1.5" /> Yapıya Dön
          </Button>
        </div>
      }
    >
      {/* Üst özet band */}
      <div className="mb-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-3 rounded-none sm:rounded-sm border-l-4 border-[#95C11F]">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Blok</div>
          <div className="font-bold text-slate-900 truncate">{meta.block_name}</div>
          <div className="text-xs text-slate-500 font-mono">{meta.block_code || "—"}</div>
        </Card>
        <Card className="p-3 rounded-none sm:rounded-sm">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Toplam Kalem</div>
          <div className="text-2xl font-bold text-[#1B4776] mt-1 font-heading" data-testid="wbs-count">{stats.count}</div>
        </Card>
        <Card className="p-3 rounded-none sm:rounded-sm md:col-span-2">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Planlanan Toplam Bedel</div>
          <div className="text-2xl font-bold text-[#95C11F] mt-1 font-mono" data-testid="wbs-total">
            ₺{stats.total.toLocaleString("tr-TR", { maximumFractionDigits: 2 })}
          </div>
        </Card>
      </div>

      {/* Toolbar */}
      {canManage && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Button onClick={addRoot} size="sm" data-testid="wbs-add-root"
                  className="rounded-none sm:rounded-sm h-8 text-xs uppercase tracking-widest bg-[#1B4776] hover:bg-[#153859] text-white">
            <Plus size={12} className="mr-1" /> Ana Kalem
          </Button>
          <Button onClick={() => setTemplateDialog(true)} size="sm" variant="outline"
                  data-testid="wbs-templates-btn"
                  className="rounded-none sm:rounded-sm h-8 text-xs uppercase tracking-widest">
            <Download size={12} className="mr-1" /> Şablondan Yükle
          </Button>
          <Button onClick={() => setSaveTplDialog(true)} size="sm" variant="outline"
                  data-testid="wbs-save-as-template"
                  disabled={items.length === 0}
                  className="rounded-none sm:rounded-sm h-8 text-xs uppercase tracking-widest">
            <Copy size={12} className="mr-1" /> Şablon Olarak Kaydet
          </Button>
          <span className="flex-1" />
          <Button onClick={expandAll} size="sm" variant="ghost"
                  className="h-8 text-xs text-slate-600">Tümünü Aç</Button>
          <Button onClick={collapseAll} size="sm" variant="ghost"
                  className="h-8 text-xs text-slate-600">Tümünü Kapat</Button>
          {items.length > 0 && (
            <Button onClick={clearAll} size="sm" variant="outline"
                    data-testid="wbs-clear-all"
                    className="rounded-none sm:rounded-sm h-8 text-xs uppercase tracking-widest text-red-600 border-red-200 hover:bg-red-50">
              <Trash size={12} className="mr-1" /> Tümünü Sil
            </Button>
          )}
          <Button onClick={save} size="sm"
                  disabled={saving || !dirty}
                  data-testid="wbs-save"
                  className="rounded-none sm:rounded-sm h-8 text-xs uppercase tracking-widest bg-[#95C11F] hover:bg-[#7ea11a] text-[#1B2A3A] font-bold disabled:opacity-40">
            <FloppyDisk size={12} className="mr-1" />
            {saving ? "Kaydediliyor..." : dirty ? "Kaydet" : "Kaydedildi"}
          </Button>
        </div>
      )}

      {dirty && (
        <div className="mb-3 bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 flex items-center gap-2">
          <Warning size={14} weight="fill" className="text-amber-600" />
          Kaydedilmemiş değişiklikler var. Ayrılmadan önce <b>Kaydet</b> düğmesine basın.
        </div>
      )}

      {loading ? (
        <div className="text-center py-16 text-sm text-slate-500">Yükleniyor…</div>
      ) : items.length === 0 ? (
        <Card className="p-12 rounded-none sm:rounded-sm text-center border-dashed border-2 border-slate-200">
          <Tree size={40} weight="duotone" className="mx-auto mb-3 text-slate-300" />
          <div className="text-sm font-bold text-slate-700 mb-1">Henüz iş kırınım kalemi yok</div>
          <div className="text-xs text-slate-500 mb-4">
            Sıfırdan başlamak için <b>Ana Kalem</b>, hazır bir yapı kullanmak için <b>Şablondan Yükle</b>&apos;ye tıklayın.
          </div>
        </Card>
      ) : (
        <Card className="rounded-none sm:rounded-sm border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-[10px] uppercase tracking-[0.2em] font-bold text-slate-600">
                <tr>
                  <th className="text-left px-2 py-2 min-w-[100px]">Kod</th>
                  <th className="text-left px-2 py-2">İmalat Adı</th>
                  <th className="text-left px-2 py-2 min-w-[80px]">Birim</th>
                  <th className="text-right px-2 py-2 min-w-[100px]">Miktar</th>
                  <th className="text-right px-2 py-2 min-w-[120px]">Birim Fiyat</th>
                  <th className="text-left px-1 py-2 min-w-[130px]">Planlanan Başlangıç</th>
                  <th className="text-left px-1 py-2 min-w-[130px]">Planlanan Bitiş</th>
                  <th className="text-right px-2 py-2 min-w-[110px]">Tutar</th>
                  {canManage && <th className="text-center px-2 py-2 min-w-[70px]">İşlem</th>}
                </tr>
              </thead>
              <tbody>
                {items.map(it => (
                  <WBSRow key={it.id} item={it} depth={0}
                          onUpdate={onUpdate} onDelete={onDelete} onAddChild={onAddChild}
                          canManage={canManage} expanded={expanded} toggle={toggle} />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Şablon Seçim Dialog */}
      <Dialog open={templateDialog} onOpenChange={setTemplateDialog}>
        <DialogContent className="rounded-none sm:rounded-sm max-w-2xl max-h-[80vh] overflow-y-auto"
                       data-testid="wbs-template-picker">
          <DialogHeader>
            <DialogTitle className="font-bold tracking-tight font-heading">Şablondan Yükle</DialogTitle>
            <DialogDescription className="text-xs text-slate-600">
              Hazır bir şablon seçin. Uygulama sonrası kalem miktarları/fiyatları düzenlenmelidir.
              Mevcut kalemler üzerine yazılır.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {templates.length === 0 && (
              <div className="text-sm text-slate-500 italic text-center py-6">Şablon bulunamadı</div>
            )}
            {templates.map(tpl => (
              <div key={tpl.id}
                   data-testid={`wbs-tpl-${tpl.id}`}
                   className="border border-slate-200 hover:border-[#95C11F] p-3 flex items-start justify-between gap-3 group">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <div className="font-bold text-slate-900">{tpl.name}</div>
                    {tpl.is_system && (
                      <span className="text-[9px] uppercase tracking-widest bg-slate-200 text-slate-600 px-1.5 py-0.5 font-bold">Sistem</span>
                    )}
                  </div>
                  {tpl.description && (
                    <div className="text-xs text-slate-500 mt-1 line-clamp-2">{tpl.description}</div>
                  )}
                  <div className="text-[10px] uppercase tracking-widest font-bold text-[#1B4776] mt-1.5">
                    {tpl.items_count} kalem
                  </div>
                </div>
                <Button onClick={() => applyTemplate(tpl)}
                        data-testid={`wbs-tpl-apply-${tpl.id}`}
                        size="sm"
                        className="rounded-none sm:rounded-sm h-8 text-xs uppercase tracking-widest bg-[#95C11F] hover:bg-[#7ea11a] text-[#1B2A3A] font-bold">
                  Uygula
                </Button>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplateDialog(false)}
                    className="rounded-none sm:rounded-sm">Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Şablon Kaydet Dialog */}
      <Dialog open={saveTplDialog} onOpenChange={setSaveTplDialog}>
        <DialogContent className="rounded-none sm:rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-bold tracking-tight font-heading">Şablon Olarak Kaydet</DialogTitle>
            <DialogDescription className="text-xs text-slate-600">
              Mevcut iş kırınımını gelecekte başka bloklarda kullanmak üzere şablon olarak kaydedin.
              Önce mevcut değişiklikleri kaydetmiş olmanız gerekir.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Şablon Adı</Label>
              <Input value={saveTplForm.name}
                     data-testid="wbs-save-tpl-name"
                     onChange={e => setSaveTplForm(f => ({...f, name: e.target.value}))}
                     placeholder="Örn: Konut Bloğu — 8 Kat"
                     className="mt-2 rounded-none sm:rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Açıklama</Label>
              <Textarea value={saveTplForm.description}
                        data-testid="wbs-save-tpl-desc"
                        onChange={e => setSaveTplForm(f => ({...f, description: e.target.value}))}
                        rows={2}
                        className="mt-2 rounded-none sm:rounded-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveTplDialog(false)}
                    className="rounded-none sm:rounded-sm">İptal</Button>
            <Button onClick={saveAsTemplate}
                    data-testid="wbs-save-tpl-confirm"
                    className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white">
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
